/**
 * Published homepage opening, with an editorial view of the connected platform.
 * The sample report appears only in the free-analysis offer.
 */
export const homeCopy = {
  sv: {
    eyebrow: "Planering som håller ihop vardagen",
    title: "AI-plattformen för hemtjänstens vardag",
    editorial: "Besöksplanering och rutter. Du godkänner.",
    lead: "Caire fördelar besöken utifrån behov, bemanning och restid — och uppdaterar planen när dagen ändras.",
    primary: "Gör en gratis analys",
    demo: "Se Nacka-demonstrationen",
    secondary: "Så fungerar Caire",
    booking: "Eller boka en genomgång",
    tour: "Ta en rundtur i plattformen",
    facts: [
      "Analysera er besöksplanering genom att ladda upp den",
      "Planeringsdata lagras i Sverige",
      "PUB-avtal tecknas i tjänsten innan ni laddar upp",
    ],
    outcomes: {
      label: "Vad ni kan följa upp",
      title: "Se hela planens konsekvenser",
      lead: "Granska resultatet för samma period och med samma definitioner. Caire visar också besök som inte får plats och varför.",
      items: [
        {
          title: "Täckning",
          body: "Se tilldelade och otilldelade besök tillsammans.",
        },
        {
          title: "Brukartid",
          body: "Se besökstid i relation till betald arbetstid.",
        },
        {
          title: "Restid",
          body: "Följ beräknade resor mellan besöken på karta.",
        },
        {
          title: "Kontinuitet",
          body: "Se hur planeringen fördelar besök mellan medarbetare.",
        },
        {
          title: "Bemanning",
          body: "Se var tillgängliga pass inte räcker till.",
        },
      ],
      link: "Läs hur måtten räknas",
    },
    connected: {
      label: "En gemensam arbetsdag",
      title: "Samma plan för kontoret, medarbetaren och närstående",
      items: [
        {
          title: "Planerare",
          body: "Se bemanning, tillgänglighet, sjukanmälningar och öppna passförfrågningar i samma plan.",
          link: "För samordnare",
          route: "solutions.scheduler",
        },
        {
          title: "Medarbetare",
          body: "Se dagens besök, ange tillgänglighet, anmäla frånvaro och svara på passförfrågningar. ETA och förseningar kan delas från arbetet i fält.",
          link: "För medarbetare",
          route: "solutions.caregivers",
        },
        {
          title: "Brukare och anhöriga",
          body: "Se tillåtna besöksuppgifter och uppdaterad ankomstinformation enligt verksamhetens behörigheter.",
          link: "Se apparna",
          route: "features.mobileApps",
        },
      ],
    },
    how: {
      title: "Från omsorgsbehov till en plan ni kan godkänna",
      link: "Slingplanering och dynamisk besöksplanering",
      lifecycle: "Besökens datalivscykel",
      steps: [
        {
          title: "Genomförandeplan",
          body: "Fråga CAIRE hjälper till att strukturera insatser och föreslå krav. En behörig medarbetare granskar och fastställer genomförandeplanen.",
        },
        {
          title: "Framåtblickande planering",
          body: "Caire väger kommande besök och arbetspass tillsammans, i planeringsomgångar med kontinuitet och kartberäknad restid. Jämför förslag för samma period.",
        },
        {
          title: "Live drift",
          body: "Frånvaro, passförfrågningar och ändringar från fältet kan ge automatiska uppdateringar inom verksamhetens regler. Olösta undantag visas öppet.",
        },
      ],
    },
    change: {
      title: "Måndag morgon: sjukanmälan löst före första besöket",
      lead: "Det är här branschen har sitt största problem: sjukanmälningar på måndagsmorgonen, strax innan dagens första besök. Caire hittar en ersättare på sekunder, planeraren godkänner och medarbetaren har sitt pass före första besöket.",
      steps: [
        {
          title: "06:40: sjukanmälan i appen",
          body: "Caire identifierar vilka besök och arbetspass som berörs.",
        },
        {
          title: "Tillgänglig ersättare eller passförfrågan",
          body: "Erbjuden tillgänglighet matchas mot omsorgsbehov, kompetens och kontinuitet. Ett accepterat appsvar kan gå vidare genom samma kontroller.",
        },
        {
          title: "Besök, pass och rutter uppdateras",
          body: "Caire optimerar den berörda planeringen med omsorgens krav och verksamhetens regler.",
        },
        {
          title: "Bekräftat pass före morgonen",
          body: "När den nya planen är tillämpad får ersättaren sitt bekräftade pass, exempelvis med start 07:00.",
        },
        {
          title: "Måndag: klart och kvar att lösa",
          body: "Planeraren ser genomförda ersättningar och öppna luckor. Hitta ersättare, jämför alternativen och skicka en förfrågan om ingen match finns.",
        },
      ],
      note: "Automatisk ersättning är på från start. Verksamheten styr reglerna; ersättaren får sitt bekräftade pass efter att planen uppdaterats. Luckor som återstår syns för planeraren.",
      illustration:
        "Illustration av flödet: frånvaro, kandidatprövning och synliga undantag.",
      link: "Så fungerar omplaneringen",
    },
    modes: {
      title: "Två sätt att använda Caire",
      items: [
        {
          title: "Caire som ert planeringssystem",
          body: "Samla insatsbehov, arbetspass och verksamhetens ramar i Caire. Plattformen tar fram planeringsförslag; behöriga användare fastställer vårdkrav och publiceringsregler.",
        },
        {
          title: "Caire med ert befintliga system",
          body: "Börja med stödda rapportexporter. Löpande koppling till ert verksamhetssystem utformas för er verksamhet; dataläsning och eventuell återföring behöver avtalas och verifieras var för sig.",
        },
      ],
      diagram: {
        label:
          "Flödesbild: insatsbehov och arbetspass går in i Caire, som ger en plan till medarbetarappen eller tillbaka till ert verksamhetssystem.",
        input: "Insatsbehov och arbetspass",
        caire: "Caire",
        out1: "Plan och medarbetarapp",
        out2: "Ert verksamhetssystem",
        out1Lines: ["Plan och", "medarbetarapp"],
        out2Lines: ["Ert", "verksamhetssystem"],
      },
      link: "Integrationer med era system",
      scheduling: "Så fungerar AI-schemaläggningen",
    },
    assist: {
      title: "Fråga CAIRE, i sitt rätta sammanhang",
      ai: {
        title: "Skriv eller prata med AI-assistenten",
        body: "I plattformen kan behöriga användare ställa operativa frågor om planeringen med text eller röst. Den öppna Fråga CAIRE på webbplatsen hjälper besökare att förstå produkten och visar inga privata omsorgsuppgifter.",
        link: "Utforska Fråga CAIRE",
      },
      app: {
        title: "Arbetsdagen i mobilen",
        body: "Medarbetare får sin besöksplanering i mobilen. Varje roll ser bara den information rollen har tillgång till.",
        link: "Se apparna och deras funktioner",
      },
    },
    trust: {
      title: "Trygghet innan första uppladdningen",
      lead: "Det här gäller innan verkliga personuppgifter behandlas.",
      items: [
        {
          icon: "file",
          title: "PUB före uppladdning",
          body: "En behörig företrädare godkänner personuppgiftsbiträdesavtalet för organisationen innan verkliga uppgifter behandlas.",
        },
        {
          icon: "server",
          title: "Planeringsdata i AWS Stockholm",
          body: "Planeringsdata lagras i AWS-regionen Stockholm. Konto och inloggning hanteras separat, se integritetspolicyn.",
        },
        {
          icon: "users",
          title: "Behörighet per roll",
          body: "Åtkomst styrs per organisation och roll. Medarbetare och anhöriga ser bara det deras roll får se.",
        },
        {
          icon: "list",
          title: "Underbiträden redovisas",
          body: "Leverantörer, regioner och överföringsskydd står i integritetspolicyn och i PUB-bilagan.",
        },
        {
          icon: "alert",
          title: "Kontakt vid incident",
          body: "Misstänker ni en säkerhetsincident? Mejla info@caire.se. Skicka inga personuppgifter om brukare i mejlet.",
        },
      ],
      link: "Gå till Trygghetscenter",
    },
    offer: {
      title: "Kostnadsfri analys av er besöksplanering",
      lead: "En vald period och en optimering ingår, och inget abonnemang startar. Ladda upp er besöksplanering, så analyserar Caire den.",
      stepsTitle: "Så går det till",
      steps: [
        "Skapa ett konto.",
        "Godkänn villkoren, integritetspolicyn och PUB-avtalet för organisationen.",
        "Ladda upp er besöksplanering: besöksrapport och brukar- och adressregister från ett verksamhetssystem som Caire stöder.",
        "Caire analyserar besöksplaneringen och räknar fram förslag. Ni får ett mejl när analysen är klar.",
      ],
      coverAlt: "Omslaget till exempelrapporten En plan som håller",
      reportLabel: "Se exempelrapporten (PDF)",
    },
    faqTitle: "Vanliga frågor om AI-schemaläggning i hemtjänsten",
    faqs: [
      {
        question: "Hur lång tid tar införandet?",
        answer:
          "Analysen kan börja med en export, utan integration. Hur lång tid löpande planering tar att införa beror på ert system, antalet enheter och vilka regler som ska ställas in. Det går vi igenom vid en genomgång.",
      },
      {
        question: "Vem äger datan?",
        answer:
          "Er organisation är personuppgiftsansvarig för uppgifter om medarbetare, brukare och besök. Caire behandlar dem som personuppgiftsbiträde enligt PUB och era instruktioner. En behörig företrädare kan begära återlämning eller radering.",
      },
      {
        question: "Vad krävs av oss?",
        answer:
          "För att se exemplet krävs varken konto eller filer. För en egen analys behövs en behörig företrädare som godkänner PUB och en export med besökshistorik och brukar- och adressregister från ett verksamhetssystem som Caire stöder. Personal och arbetspass är valfria.",
      },
      {
        question: "Vad kostar det efter analysen?",
        answer:
          "Analysen är kostnadsfri och inget abonnemang startar automatiskt. Löpande planering kostar 9 900 kr per månad för 1–33 aktiva anställda, därefter 300 kr per aktiv anställd och månad, exklusive moms.",
      },
      {
        question: "Måste vi byta verksamhetssystem?",
        answer:
          "Nej. Använd Caire fristående eller som integrerad optimerare. Stödda exporter används för första analysen; löpande integration och drift anpassas till ert system och valda upplägg.",
      },
      {
        question: "Kan AI lägga schema i hemtjänsten?",
        answer:
          "Ja. Att lägga schema med AI innebär att Caire fördelar besöken på arbetspassen och planerar rutterna utifrån insatserna, medarbetarnas kompetens och restiderna mellan adresserna. Att göra schema med AI i Caire börjar med insatsbehov och arbetspass. Caire räknar fram förslag för perioden, och planeraren jämför dem med samma mått.",
      },
      {
        question:
          "Vad är skillnaden mellan automatisk schemaläggning och AI-schemaläggning?",
        answer:
          "Automatisk schemaläggning brukar betyda att systemet upprepar ett mönster: samma pass, samma dag, samma person. AI-schemaläggning löser i stället ett optimeringsproblem och väger brukartid, restid, kontinuitet, kompetenskrav och arbetstidsregler mot varandra. Skillnaden märks när något ändras: ett upprepat mönster rättas för hand, en optimering räknas om.",
      },
      {
        question: "Vad är autoschema, och passar det vård och omsorg?",
        answer:
          "Autoschema väger samman medarbetarnas önskemål till ett bemanningsschema, alltså vem som jobbar när. Inom vård och omsorg löser AI-schemaläggning nästa steg: vilka besök varje pass ska innehålla och i vilken ordning. De hänger ihop, eftersom bemanningen är det utbud besöken ska rymmas i, men det är inte samma problem.",
      },
      {
        question: "Vad sköter Caire automatiskt?",
        answer:
          "När insatsbehov, arbetspass och verksamhetens ramar är inställda optimerar Caire besöksplanering och rutter löpande. Förändringar i behov och bemanning tas med i realtid. Planerarnas fastställda placeringar och verksamhetens regler styr vad som får ändras.",
      },
    ],
    closeTitle: "Hela planeringen börjar här",
    closeBody:
      "Prova Caire med en kostnadsfri analys, eller boka en genomgång av hur plattformen knyter ihop planering, bemanning och arbetet i fält.",
    about: "Möt grundarna bakom Caire",
  },
  en: {
    eyebrow: "Planning for the working day",
    title: "The AI platform for everyday home care",
    editorial: "Visit planning and routes. You approve.",
    lead: "Caire assigns visits from care needs, staffing and travel time — and updates the plan when the day changes.",
    primary: "Get a free analysis",
    demo: "View the Nacka demo",
    secondary: "How Caire works",
    booking: "Or book a walkthrough",
    tour: "Take a tour of the platform",
    facts: [
      "Analyse your schedule by uploading it",
      "Planning data stored in Sweden",
      "DPA signed in the service before you upload",
    ],
    outcomes: {
      label: "What you can assess",
      title: "See the consequences of the whole plan",
      lead: "Review results for the same period using the same definitions. Caire also shows visits that do not fit and why.",
      items: [
        {
          title: "Coverage",
          body: "See assigned and unassigned visits together.",
        },
        {
          title: "Client time",
          body: "Relate visit time to paid working time.",
        },
        {
          title: "Travel",
          body: "Follow map-calculated travel between visits.",
        },
        {
          title: "Continuity",
          body: "See how visits are distributed among employees.",
        },
        {
          title: "Staffing",
          body: "See where available shifts do not cover demand.",
        },
      ],
      link: "How the measures are calculated",
    },
    connected: {
      label: "One connected working day",
      title: "One plan for the office, employees and families",
      items: [
        {
          title: "Planners",
          body: "See staffing, availability, sick reports and open shift requests in one plan.",
          link: "For coordinators",
          route: "solutions.scheduler",
        },
        {
          title: "Employees",
          body: "See today's visits, share availability, report absence and answer shift requests. ETA and delays can be shared from field work.",
          link: "For caregivers",
          route: "solutions.caregivers",
        },
        {
          title: "Clients and families",
          body: "See permitted visit details and updated arrival information within your organisation's permissions.",
          link: "Explore the apps",
          route: "features.mobileApps",
        },
      ],
    },
    how: {
      title: "From care needs to a plan you can approve",
      link: "Recurring routes and dynamic visit planning",
      lifecycle: "The visit data lifecycle",
      steps: [
        {
          title: "Care plan",
          body: "Ask CAIRE helps structure care tasks and suggest requirements. An authorised employee reviews and confirms the care plan.",
        },
        {
          title: "Forward planning",
          body: "Caire plans coming visits and shifts together in batches, considering continuity and map-calculated travel. Compare proposals for the same period.",
        },
        {
          title: "Live operations",
          body: "Absence, shift requests and field changes can trigger automatic updates within operating rules. Unresolved exceptions remain visible.",
        },
      ],
    },
    change: {
      title: "Monday morning: a sick call sorted before the first visit",
      lead: "This is where home care hurts most: sick calls on Monday morning, just before the first visits. Caire finds a replacement in seconds, the planner approves and the caregiver has their shift before the first visit.",
      steps: [
        {
          title: "06:40: sickness reported in the app",
          body: "Caire identifies the affected visits and shifts.",
        },
        {
          title: "Available replacement or shift request",
          body: "Offered availability is matched against care needs, skills and continuity. An accepted app request can follow the same checks.",
        },
        {
          title: "Visits, shifts and routes updated",
          body: "Caire optimises the affected planning within care requirements and your organisation’s rules.",
        },
        {
          title: "A confirmed shift before the morning",
          body: "Once the new plan is applied, the replacement receives a confirmed shift, for example starting at 07:00.",
        },
        {
          title: "Monday: completed work and remaining gaps",
          body: "The planner sees completed replacements and open gaps. Find a replacement, compare options and send a request when no match is available.",
        },
      ],
      note: "Automatic replacement is on by default. Your organisation controls the rules; the replacement receives a confirmed shift after the plan is updated. Remaining gaps stay visible to the planner.",
      illustration:
        "Diagram of absence, candidate checks and visible exceptions.",
      link: "How replanning works",
    },
    modes: {
      title: "Two ways to use Caire",
      items: [
        {
          title: "Caire as your planning system",
          body: "Keep care needs, shifts and operating rules in Caire. The platform prepares proposals; authorised staff confirm care requirements and publication rules.",
        },
        {
          title: "Caire with your existing system",
          body: "Start with supported report exports. An ongoing connection to your existing system is designed for your organisation; data access and any return flow must be agreed and verified separately.",
        },
      ],
      diagram: {
        label:
          "Flow diagram: care needs and shifts go into Caire, which returns a plan to the employee app or back to your existing system.",
        input: "Care needs and shifts",
        caire: "Caire",
        out1: "Plan and employee app",
        out2: "Your existing system",
        out1Lines: ["Plan and", "employee app"],
        out2Lines: ["Your existing", "system"],
      },
      link: "Integrations with your systems",
      scheduling: "How AI scheduling works",
    },
    assist: {
      title: "Ask CAIRE, in the right context",
      ai: {
        title: "Type or talk to your AI assistant",
        body: "Authorised users can ask operational planning questions by text or voice inside the platform. Public Ask CAIRE on this website helps visitors understand the product and does not show private care information.",
        link: "Explore Ask CAIRE",
      },
      app: {
        title: "The working day on the phone",
        body: "Employees have their visit plan on their phone. Each role sees only the information it is authorised to access.",
        link: "Explore the apps and their features",
      },
    },
    trust: {
      title: "Trust before the first upload",
      lead: "This applies before any real personal data is processed.",
      items: [
        {
          icon: "file",
          title: "DPA before upload",
          body: "An authorised representative accepts the data processing agreement for the organisation before real data is processed.",
        },
        {
          icon: "server",
          title: "Planning data in AWS Stockholm",
          body: "Planning data is stored in the AWS Stockholm region. Accounts and sign-in are handled separately; see the privacy policy.",
        },
        {
          icon: "users",
          title: "Access by role",
          body: "Access is scoped by organisation and role. Employees and relatives see only what their role may see.",
        },
        {
          icon: "list",
          title: "Subprocessors disclosed",
          body: "Providers, regions and transfer safeguards are listed in the privacy policy and the DPA annex.",
        },
        {
          icon: "alert",
          title: "Incident contact",
          body: "Suspect a security incident? Email info@caire.se. Do not include client personal data in the email.",
        },
      ],
      link: "Visit the trust center",
    },
    offer: {
      title: "Complimentary analysis of your visit planning",
      lead: "One selected period and one optimisation are included, and no subscription starts. Upload your schedule and Caire analyses it.",
      stepsTitle: "What happens next",
      steps: [
        "Create an account.",
        "Accept the terms, the privacy policy and the DPA for your organisation.",
        "Upload your schedule: the visit report and the client and address register from a care system Caire supports.",
        "Caire analyses the schedule and calculates proposals. You receive an email when the analysis is ready.",
      ],
      coverAlt: "Cover of the sample report A plan that holds",
      reportLabel: "See the sample report (PDF)",
    },
    faqTitle: "Questions about AI scheduling in home care",
    faqs: [
      {
        question: "How long does implementation take?",
        answer:
          "The analysis can start from an export, without an integration. How long ongoing planning takes to introduce depends on your system, the number of units and the rules to configure. We go through it in a walkthrough.",
      },
      {
        question: "Who owns the data?",
        answer:
          "Your organisation is the controller for data about employees, clients and visits. Caire processes it as a processor under the DPA and your instructions. An authorised representative can request return or deletion.",
      },
      {
        question: "What do you need from us?",
        answer:
          "Viewing the example needs no account or files. Your own analysis needs an authorised representative to accept the DPA and an export with visit history and the client and address register from a care system Caire supports. Staff and shifts are optional.",
      },
      {
        question: "What does it cost after the analysis?",
        answer:
          "The analysis is complimentary and no subscription starts automatically. Ongoing planning costs SEK 9,900 per month for 1–33 active employees, then SEK 300 per active employee per month, excluding VAT.",
      },
      {
        question: "Do we need to replace our existing system?",
        answer:
          "No. Use Caire independently or as an integrated optimiser. Supported exports are used for the initial analysis; ongoing integration and operations follow your system and chosen setup.",
      },
      {
        question: "Can AI build a home care schedule?",
        answer:
          "Yes. Building a schedule with AI means Caire distributes visits across shifts and plans routes from care tasks, employee skills and the travel times between addresses. In Caire it starts with care needs and shifts. Caire calculates proposals for the period, and the planner compares them on the same measures.",
      },
      {
        question:
          "What is the difference between automatic scheduling and AI scheduling?",
        answer:
          "Automatic scheduling usually means the system repeats a pattern: same shift, same day, same person. AI scheduling instead solves an optimisation problem, weighing client-facing time, travel time, continuity, skill requirements and working-time rules against each other. The difference shows when something changes: a repeated pattern is corrected by hand, an optimisation is recalculated.",
      },
      {
        question:
          "What is autoschema, and does it suit health and social care?",
        answer:
          "Autoschema combines employee preferences into a staffing schedule: who works when. In health and social care, AI scheduling solves the next step: which visits each shift contains and in what order. They are connected, since staffing is the supply the visits must fit into, but they are not the same problem.",
      },
      {
        question: "What does Caire run automatically?",
        answer:
          "Once care needs, shifts and operational rules are configured, Caire continuously optimises visit planning and routes. Changes in needs and staffing are accounted for in real time. Planner-set placements and your configured rules govern what may change.",
      },
    ],
    closeTitle: "The whole planning journey starts here",
    closeBody:
      "Try Caire with a complimentary analysis, or book a walkthrough of how the platform connects planning, staffing and field work.",
    about: "Meet the founders behind Caire",
  },
} as const;

export type HomeCopy = (typeof homeCopy)["sv"] | (typeof homeCopy)["en"];
